import { React, useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../services/auth.service';
import { Eye, EyeSlash } from 'react-bootstrap-icons';
import { CompanyLogo } from '../../components/companyLogo';
import { Alert } from 'antd';
import ReCAPTCHA from 'react-google-recaptcha';
import '../../styles/auth.scss';

export const LoginPage = () => {
    const { login } = useContext(AuthContext);
    const navigate = useNavigate();
    const recaptchaRef = useRef(null);
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [loginResult, setLoginResult] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [captchaToken, setCaptchaToken] = useState("");

    const handleInputChange = (event) => {
        const { name, value } = event.target;

        switch (name) {
            case "username":
                setUsername(value);
                break;
            case "password":
                setPassword(value);
                break;
            default:
        }
    };

    const handleRecaptchaChange = (token) => {
        setCaptchaToken(token);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!captchaToken) {
            alert("Please complete the reCAPTCHA.");
            return;
        }

        const response = await login({
            username,
            password,
            captchaToken,
        });

        setLoginResult(response?.result);

        if (response?.result) {
            navigate("/");
        } else {
            // Reset reCAPTCHA if login fails
            recaptchaRef.current?.reset();
            setCaptchaToken("");
        }
    };

    useEffect(() => {
        // Check for success message flag
        const wasPasswordJustSet = localStorage.getItem('passwordSetupSuccess');
        if (wasPasswordJustSet === 'true') {
            setShowSuccessMessage(true);
            // Remove the flag after showing the message
            localStorage.removeItem('passwordSetupSuccess');
        }
    }, []);

    const handleDismissSuccessMessage = () => {
        setShowSuccessMessage(false);
    };

    return (
        <div className='auth-container login-page'>
            <div className="bottom-circle"></div>
            <div className="auth-form-container">
                <div className="text-center mb-2">
                    <CompanyLogo />
                </div>
                {!showSuccessMessage && (
                    <h1 className='auth-heading text-center'>Log in</h1>
                )}
                
                {showSuccessMessage && (
                    <div className="success-container">
                        <div className="success-title">
                            Your account has been successfully created! 🎉
                        </div>
                        <div className="success-description">
                            Please log in to access the portal
                        </div>
                    </div>
                )}
        
                {!loginResult && (
                    <Alert
                        message="Login Failed"
                        description="Please enter a correct username and password. Note that both fields may be case-sensitive."
                        type="error"
                        showIcon
                        className="auth-alert"
                    />
                )}
        
                <form method="POST" className="mt-4" onSubmit={handleSubmit}>
                    <div className="mb-3">
                        <input
                            type="text"
                            autoFocus
                            autoCapitalize="none"
                            autoComplete="username"
                            maxLength="150"
                            className="auth-input"
                            required
                            id="id_auth-username"
                            placeholder="Email address"
                            name="username"
                            value={username}
                            onChange={handleInputChange} 
                        />
                    </div>
                    <div className="password-field-container">
                        <input
                            autoComplete="current-password"
                            className="auth-input"
                            required
                            id="id_auth-password"
                            placeholder="Password"
                            name="password"
                            value={password}
                            onChange={handleInputChange}
                            type={showPassword ? "text" : "password"}
                        />
                        <label className='eye-slash-icon' htmlFor="check">
                            {showPassword ? (
                                <Eye />
                            ) : (
                                <EyeSlash />
                            )}
                        </label>
                        <input
                            id="check"
                            type="checkbox"
                            value={showPassword}
                            onChange={() => setShowPassword((prev) => !prev)}
                            style={{ display: 'none' }}
                        />
                    </div>

                    <div className="mt-3">
                        <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey="6Ldi_i4rAAAAAFCkkEM4jMWTrnKbVZ4OggTzxKiV" // Replace with your reCAPTCHA site key
                            onChange={handleRecaptchaChange}
                        />
                    </div>
        
                    <div className="mt-4">
                        <button type="submit" className="auth-button" disabled={!captchaToken}>
                            Log in
                        </button>
                    </div>
                    <div className="mt-4">
                        <a className="auth-secondary-button d-block text-center" href="/password-reset">Forgot Password?</a>
                    </div>
                </form>
            </div>
        </div>
    );
};